import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight, Sun, ShieldCheck, BadgeIndianRupee, Wrench, Home as HomeIcon,
  Factory, FileCheck, Zap, Star, Quote,
} from "lucide-react";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import Counter from "@/components/Counter";

const HERO_IMG = "https://images.pexels.com/photos/12243093/pexels-photo-12243093.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const ease = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function SunBadge() {
  return (
    <div className="relative w-28 h-28 md:w-36 md:h-36">
      <svg viewBox="0 0 100 100" className="w-full h-full animate-spin-slower">
        <defs>
          <path id="badge-circle" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
        </defs>
        <text className="fill-gold font-mono" style={{ fontSize: 8.2, letterSpacing: 2.4 }}>
          <textPath href="#badge-circle">SAHI SOCH • SAHI SAVINGS • SOLARSOCH •</textPath>
        </text>
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gold sun-glow flex items-center justify-center">
          <Sun size={26} className="text-brand-deep" strokeWidth={2.4} />
        </div>
      </div>
    </div>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative grain overflow-hidden bg-brand-deep text-white min-h-[100svh] flex items-center pt-24 pb-16">
      <div className="absolute -top-40 -right-40 w-[34rem] h-[34rem] rounded-full bg-gold/10 blur-3xl" />
      <div className="absolute bottom-0 -left-52 w-[30rem] h-[30rem] rounded-full bg-brand-light/20 blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full grid lg:grid-cols-12 gap-12 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7, ease }}
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 mb-8"
            data-testid="hero-subsidy-badge"
          >
            <BadgeIndianRupee size={15} className="text-gold" />
            <span className="text-xs md:text-sm font-semibold text-gold tracking-wide">
              PM Surya Ghar Subsidy — up to ₹78,000
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold leading-[0.95] tracking-tight text-[13.5vw] sm:text-6xl lg:text-7xl xl:text-[5.2rem]">
            <MaskedLine delay={0.25}>Your roof,</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-gold">your power</span> plant.
            </MaskedLine>
            <MaskedLine delay={0.55} className="text-stroke-gold">Sahi Soch.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease }}
            className="mt-6 max-w-xl text-base md:text-lg text-white/70 leading-relaxed"
          >
            SolarSoch turns your rooftop into a private power plant — Tier-1 N-Type TOPCon
            panels, end-to-end DISCOM handling, and 25 years of people who stay with you
            long after the last panel is installed.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8, ease }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link
              to="/calculator"
              data-testid="hero-cta-calculator"
              className="group inline-flex items-center gap-2 rounded-full bg-gold text-brand-deep px-7 py-3.5 font-bold text-sm md:text-base transition-transform hover:scale-105 active:scale-95"
            >
              Calculate My Savings
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-survey"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-7 py-3.5 font-bold text-sm md:text-base hover:border-gold hover:text-gold transition-colors"
            >
              Book Free Rooftop Survey
            </Link>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.6, duration: 1.1, ease }}
            className="relative rounded-[2rem] overflow-hidden border border-white/15 shadow-[0_40px_80px_rgba(0,0,0,0.4)]"
          >
            <motion.img
              src={HERO_IMG}
              alt="Rooftop solar panels on a home"
              style={{ y: imgY, scale: 1.18 }}
              className="w-full h-[22rem] md:h-[30rem] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/80 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] text-gold">LIVE INSTALL</p>
                <p className="font-display font-bold text-lg">Koramangala, Bengaluru</p>
              </div>
              <p className="font-mono text-xs text-white/70">5 kW · TOPCon</p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.1, type: "spring", stiffness: 200, damping: 16 }}
            className="absolute -top-8 -left-4 md:-left-10"
          >
            <SunBadge />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

const metrics = [
  { value: 500, suffix: "+", label: "kW Installed" },
  { value: 320, suffix: "+", label: "Happy Families" },
  { value: 1200, suffix: " t", label: "CO₂ Offset" },
  { value: 78000, prefix: "₹", suffix: "", label: "Max Subsidy Secured" },
];

function MetricsBanner() {
  return (
    <section data-testid="metrics-banner" className="bg-gold">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
        {metrics.map((m, i) => (
          <Reveal key={m.label} delay={i * 0.08} className="text-center md:text-left">
            <p className="font-display font-extrabold text-3xl md:text-4xl text-brand-deep">
              <Counter value={m.value} prefix={m.prefix || ""} suffix={m.suffix} />
            </p>
            <p className="mt-1 font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-brand-deep/70">{m.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const chapters = [
  {
    num: "01", icon: Sun, title: "Precision Tech",
    text: "Tier-1 N-Type TOPCon panels with higher efficiency in Indian heat, premium inverters, and structures engineered for 25+ years of monsoon and sun.",
  },
  {
    num: "02", icon: BadgeIndianRupee, title: "Maximum Savings",
    text: "We squeeze every rupee — PM Surya Ghar subsidy up to ₹78,000, net metering done right, and system sizing matched to your actual bill, not a sales target.",
  },
  {
    num: "03", icon: ShieldCheck, title: "Lifetime Care",
    text: "Engineers who treat your home like their own, proactive maintenance, and a support line that answers — for the full 25-year life of your plant.",
  },
];

function Manifesto() {
  return (
    <section data-testid="manifesto-section" className="max-w-7xl mx-auto px-4 sm:px-6 py-24 md:py-32">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-brand mb-4">THE SAHI SOCH EDGE</p>
        <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink max-w-3xl leading-[1.02]">
          Going solar should feel like a <span className="text-brand">relief</span> — not a risk.
        </h2>
      </Reveal>

      <div className="mt-16 grid md:grid-cols-3 gap-6">
        {chapters.map((c, i) => (
          <Reveal key={c.num} delay={i * 0.12}>
            <div
              data-testid={`manifesto-chapter-${c.num}`}
              className="group relative h-full rounded-3xl bg-white border border-brand/10 p-8 overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_60px_rgba(3,59,43,0.14)]"
            >
              <span className="font-display font-extrabold text-6xl text-brand/10 group-hover:text-gold/60 transition-colors duration-500">{c.num}</span>
              <c.icon size={30} className="mt-6 text-brand group-hover:text-gold transition-colors duration-500" strokeWidth={2} />
              <h3 className="mt-4 font-display font-bold text-2xl text-ink">{c.title}</h3>
              <p className="mt-3 text-sm md:text-base text-moss leading-relaxed">{c.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const serviceTabs = [
  {
    id: "residential", icon: HomeIcon, label: "Residential",
    title: "Rooftop Solar for Homes",
    text: "Complete home systems from 1 kW to 10 kW — survey, design, installation, subsidy paperwork and net metering, all handled by one team.",
    points: ["Free rooftop survey & shadow analysis", "PM Surya Ghar subsidy filing", "Net meter installation with DISCOM"],
  },
  {
    id: "commercial", icon: Factory, label: "Commercial",
    title: "High-Yield C&I Solar",
    text: "Factories, warehouses, offices and institutions — engineered for maximum generation per square foot with accelerated depreciation benefits.",
    points: ["Custom engineering & load study", "Accelerated depreciation advisory", "Remote generation monitoring"],
  },
  {
    id: "care", icon: Wrench, label: "Care & AMC",
    title: "25-Year Maintenance",
    text: "Panel cleaning, health checks, inverter servicing and generation audits that keep your plant performing like day one.",
    points: ["Scheduled panel cleaning", "Annual generation audit", "Priority breakdown support"],
  },
];

function ServicesPreview() {
  const [activeId, setActiveId] = useState("residential");
  const active = serviceTabs.find((t) => t.id === activeId);

  return (
    <section data-testid="services-preview" className="bg-brand-mist/60 py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs tracking-[0.35em] text-brand mb-4">WHAT WE DO</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-ink">
              One team. <span className="text-brand">Every step.</span>
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="services-preview-view-all"
            className="group inline-flex items-center gap-2 font-bold text-brand text-sm"
          >
            View all services
            <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex gap-2 overflow-x-auto pb-2" role="tablist" data-testid="services-tab-list">
            {serviceTabs.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={activeId === t.id}
                data-testid={`services-tab-${t.id}`}
                onClick={() => setActiveId(t.id)}
                className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold whitespace-nowrap transition-all ${
                  activeId === t.id
                    ? "bg-brand text-white shadow-lg"
                    : "bg-white text-moss border border-brand/15 hover:border-brand"
                }`}
              >
                <t.icon size={16} /> {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div
          key={active.id}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease }}
          className="mt-8 rounded-3xl bg-white border border-brand/10 p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center"
          data-testid="services-tab-panel"
        >
          <div>
            <h3 className="font-display font-bold text-2xl md:text-3xl text-ink">{active.title}</h3>
            <p className="mt-4 text-moss leading-relaxed">{active.text}</p>
            <ul className="mt-6 space-y-3">
              {active.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm font-semibold text-ink">
                  <Zap size={16} className="text-gold mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl bg-brand-deep p-8 text-white">
            <p className="font-mono text-[10px] tracking-[0.3em] text-gold">TYPICAL OUTCOME</p>
            <p className="mt-3 font-display font-extrabold text-4xl text-gold">90%</p>
            <p className="mt-1 text-white/70 text-sm">average reduction in monthly electricity bill after SolarSoch installation.</p>
            <Link
              to="/calculator"
              data-testid="services-tab-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold text-brand-deep px-5 py-2.5 text-sm font-bold hover:scale-105 transition-transform"
            >
              Estimate my savings <ArrowUpRight size={15} />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const spotlight = [
  { size: "3 kW", tag: "Subsidized Home", ideal: "2–3 BHK homes", price: "₹1,45,000*", subsidy: "₹78,000 subsidy" },
  { size: "5 kW", tag: "Popular Choice", ideal: "3–4 BHK / duplex", price: "₹2,35,000*", subsidy: "₹78,000 subsidy" },
  { size: "10 kW", tag: "High Capacity", ideal: "Villas & business", price: "₹4,50,000*", subsidy: "Custom rebate" },
];

function OfferingsSpotlight() {
  return (
    <section data-testid="offerings-spotlight" className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono text-xs tracking-[0.35em] text-brand mb-4">SOLAR OFFERINGS</p>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-ink">
            Pick your <span className="text-brand">power size.</span>
          </h2>
        </div>
        <Link
          to="/offerings"
          data-testid="offerings-spotlight-view-all"
          className="group inline-flex items-center gap-2 font-bold text-brand text-sm"
        >
          Compare all packages
          <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </Reveal>

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {spotlight.map((p, i) => (
          <Reveal key={p.size} delay={i * 0.1}>
            <Link
              to="/offerings"
              data-testid={`spotlight-card-${p.size.replace(" ", "-").toLowerCase()}`}
              className={`group block h-full rounded-3xl p-8 border transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_60px_rgba(3,59,43,0.16)] ${
                i === 1 ? "bg-brand text-white border-brand" : "bg-white text-ink border-brand/10"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`rounded-full px-3 py-1 text-[11px] font-bold tracking-wide ${i === 1 ? "bg-gold text-brand-deep" : "bg-gold-soft text-brand"}`}>
                  {p.tag}
                </span>
                <Sun size={22} className={i === 1 ? "text-gold" : "text-brand"} />
              </div>
              <p className="mt-6 font-display font-extrabold text-5xl">{p.size}</p>
              <p className={`mt-1 text-sm ${i === 1 ? "text-white/70" : "text-moss"}`}>Ideal for {p.ideal}</p>
              <div className={`mt-6 pt-6 border-t ${i === 1 ? "border-white/15" : "border-brand/10"}`}>
                <p className="font-display font-bold text-2xl">{p.price}</p>
                <p className={`mt-1 text-xs font-semibold ${i === 1 ? "text-gold" : "text-brand"}`}>{p.subsidy}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const testimonials = [
  {
    name: "Ramesh Kulkarni", place: "HSR Layout, Bengaluru", system: "5 kW",
    text: "My bill dropped from ₹6,200 to under ₹500. SolarSoch handled the BESCOM net metering completely — I didn't visit a single office.",
  },
  {
    name: "Priya Shetty", place: "Whitefield, Bengaluru", system: "3 kW",
    text: "The subsidy of ₹78,000 came through exactly as they promised. Clean installation, polite engineers, zero mess on my terrace.",
  },
  {
    name: "Anand Rao", place: "Electronic City, Bengaluru", system: "10 kW",
    text: "We run our entire office floor on solar now. Their monitoring dashboard and AMC visits keep generation exactly on target.",
  },
];

function Testimonials() {
  return (
    <section data-testid="testimonials-section" className="bg-brand-deep text-white py-24 grain relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-gold mb-4">SUNNY STORIES</p>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight max-w-2xl">
            Families already saving with the sun.
          </h2>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <figure
                data-testid={`testimonial-card-${i}`}
                className="h-full rounded-3xl bg-white/5 border border-white/10 p-7 backdrop-blur-sm transition-colors hover:border-gold/40"
              >
                <Quote size={26} className="text-gold" />
                <blockquote className="mt-4 text-sm md:text-base text-white/80 leading-relaxed">{t.text}</blockquote>
                <figcaption className="mt-6 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-sm">{t.name}</p>
                    <p className="text-xs text-white/50">{t.place}</p>
                  </div>
                  <span className="rounded-full bg-gold/15 text-gold px-3 py-1 text-xs font-bold font-mono">{t.system}</span>
                </figcaption>
                <div className="mt-4 flex gap-1 text-gold">
                  {Array.from({ length: 5 }).map((_, s) => <Star key={s} size={13} fill="currentColor" />)}
                </div>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomeCta() {
  return (
    <section data-testid="home-cta" className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2.5rem] bg-gold px-8 py-14 md:p-16 text-center">
          <Sun size={340} className="absolute -right-20 -bottom-24 text-brand/10" strokeWidth={1} />
          <p className="font-mono text-xs tracking-[0.35em] text-brand-deep/70">NO COST. NO OBLIGATION.</p>
          <h2 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-deep leading-[1.02]">
            Your roof is earning<br className="hidden sm:block" /> nothing today.
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-brand-deep/75 font-medium">
            Book a free rooftop survey. Our engineer visits, measures, and gives you an exact
            generation & savings report — before you spend a rupee.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="home-cta-book-survey"
              className="inline-flex items-center gap-2 rounded-full bg-brand-deep text-gold px-8 py-4 font-bold hover:scale-105 transition-transform"
            >
              Book Free Survey <ArrowUpRight size={18} />
            </Link>
            <a
              href="tel:+919619613720"
              data-testid="home-cta-call"
              className="inline-flex items-center gap-2 rounded-full border-2 border-brand-deep/30 text-brand-deep px-8 py-4 font-bold hover:bg-brand-deep hover:text-gold transition-colors"
            >
              Call +91 96196 13720
            </a>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <MetricsBanner />
      <Manifesto />
      <Marquee
        items={[
          "PM Surya Ghar Subsidy ₹78,000", "Tier-1 N-Type TOPCon Panels",
          "25-Year Performance", "Net Metering Experts", "End-to-End DISCOM Handling",
          "Sahi Soch ☀ Sahi Savings",
        ]}
      />
      <ServicesPreview />
      <OfferingsSpotlight />
      <Testimonials />
      <HomeCta />
    </div>
  );
}
